import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

interface Metrics {
  summary: {
    events: {
      pending_events: number;
      processing_events: number;
      completed_events: number;
      failed_events: number;
    };
    drafts: {
      pending_drafts: number;
      approved_drafts: number;
      rejected_drafts: number;
      published_drafts: number;
      avg_confidence: number;
      avg_quality: number;
    };
  };
  timeSeries: Array<{
    metric_date: string;
    events_received: number;
    events_processed: number;
    drafts_created: number;
    drafts_approved: number;
    avg_confidence: number;
    avg_processing_time: number;
  }>;
}

export const AHTLMetrics: React.FC = () => {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'hour' | 'day' | 'week' | 'month'>('day');
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 seconds

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, refreshInterval);
    return () => clearInterval(interval);
  }, [period, refreshInterval]);

  const fetchMetrics = async () => {
    try {
      const response = await fetch(`/api/ahtl/metrics?period=${period}`);
      const data = await response.json();
      setMetrics(data);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !metrics) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const eventStatusData = [
    { name: 'Pending', value: metrics.summary.events.pending_events, color: '#FCD34D' },
    { name: 'Processing', value: metrics.summary.events.processing_events, color: '#60A5FA' },
    { name: 'Completed', value: metrics.summary.events.completed_events, color: '#34D399' },
    { name: 'Failed', value: metrics.summary.events.failed_events, color: '#F87171' }
  ];

  const draftStatusData = [
    { name: 'Pending', value: metrics.summary.drafts.pending_drafts, color: '#FCD34D' },
    { name: 'Approved', value: metrics.summary.drafts.approved_drafts, color: '#34D399' },
    { name: 'Rejected', value: metrics.summary.drafts.rejected_drafts, color: '#F87171' },
    { name: 'Published', value: metrics.summary.drafts.published_drafts, color: '#A78BFA' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                📊 AHTL Metrics Dashboard
              </h1>
              <p className="text-gray-600">
                Real-time monitoring of the AI Adjuster / Human Taster Loop pipeline
              </p>
            </div>
            <div className="flex space-x-3">
              {/* Period Selector */}
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value as any)}
                className="px-4 py-2 border rounded-lg bg-white"
              >
                <option value="hour">Hourly</option>
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
              </select>
              
              {/* Refresh Interval */}
              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
                className="px-4 py-2 border rounded-lg bg-white"
              >
                <option value="10000">10s refresh</option>
                <option value="30000">30s refresh</option>
                <option value="60000">1m refresh</option>
                <option value="300000">5m refresh</option>
              </select>

              <button
                onClick={fetchMetrics}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                🔄 Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm border p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Total Events</span>
              <span className="text-2xl">📥</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {Object.values(metrics.summary.events).reduce((a, b) => a + b, 0)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Last 7 days
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm border p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Total Drafts</span>
              <span className="text-2xl">📝</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {metrics.summary.drafts.pending_drafts + 
               metrics.summary.drafts.approved_drafts + 
               metrics.summary.drafts.rejected_drafts + 
               metrics.summary.drafts.published_drafts}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Last 7 days
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm border p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Avg Confidence</span>
              <span className="text-2xl">🎯</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {((metrics.summary.drafts.avg_confidence || 0) * 100).toFixed(1)}%
            </div>
            <div className={`text-xs mt-1 ${
              (metrics.summary.drafts.avg_confidence || 0) >= 0.8 ? 'text-green-600' : 'text-yellow-600'
            }`}>
              {(metrics.summary.drafts.avg_confidence || 0) >= 0.8 ? 'High confidence' : 'Moderate confidence'}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-sm border p-6"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Avg Quality</span>
              <span className="text-2xl">⭐</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {((metrics.summary.drafts.avg_quality || 0) * 100).toFixed(1)}%
            </div>
            <div className={`text-xs mt-1 ${
              (metrics.summary.drafts.avg_quality || 0) >= 0.8 ? 'text-green-600' : 'text-yellow-600'
            }`}>
              {(metrics.summary.drafts.avg_quality || 0) >= 0.8 ? 'High quality' : 'Moderate quality'}
            </div>
          </motion.div>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Event Status Distribution */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Status Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={eventStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {eventStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Draft Status Distribution */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Draft Status Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={draftStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {draftStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Time Series Chart */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={metrics.timeSeries}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="metric_date" 
                tickFormatter={(value) => new Date(value).toLocaleDateString()}
              />
              <YAxis />
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="events_received" 
                stroke="#3B82F6" 
                name="Events Received"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="events_processed" 
                stroke="#10B981" 
                name="Events Processed"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="drafts_created" 
                stroke="#F59E0B" 
                name="Drafts Created"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="drafts_approved" 
                stroke="#8B5CF6" 
                name="Drafts Approved"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Performance Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Confidence Scores Over Time */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Confidence Trend</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={metrics.timeSeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="metric_date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <YAxis 
                  domain={[0, 1]}
                  tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value: number) => `${(value * 100).toFixed(1)}%`}
                />
                <Line 
                  type="monotone" 
                  dataKey="avg_confidence" 
                  stroke="#10B981" 
                  name="Avg Confidence"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Processing Time */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Speed</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={metrics.timeSeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="metric_date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <YAxis 
                  tickFormatter={(value) => `${value}ms`}
                />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value: number) => `${value}ms`}
                />
                <Bar 
                  dataKey="avg_processing_time" 
                  fill="#3B82F6" 
                  name="Avg Processing Time"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* System Health */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">System Health</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <div className="text-sm text-gray-500">Event Processing</div>
                <div className="text-lg font-semibold text-gray-900">Healthy</div>
              </div>
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <div className="text-sm text-gray-500">AI Service</div>
                <div className="text-lg font-semibold text-gray-900">Active</div>
              </div>
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <div className="text-sm text-gray-500">Webhooks</div>
                <div className="text-lg font-semibold text-gray-900">Operational</div>
              </div>
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};